<template>
  <div>
    <v-overlay :value="isBusy">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <v-card-text>
      <div class="d-flex my-auto">
        <div class="text-body-1 my-auto mr-2">Cobb Egg Picker</div>
        <v-spacer />
        <div class="mr-2 text-body-1 my-auto ">v{{ appVersion.version }}</div>
        <v-spacer />
        <img 
          class="mt-1" 
          src="@/assets/img/cobb-logo.png" 
          alt="Cobb Logo"
          height="38"
        />
      </div>

      <v-divider class="mt-4 mb-2" />

      <div class="d-flex my-auto">
        <div :class="`mt-2 mr-1 text-${breakpoint === 'xs' ? 'subtitle-1' : 'h6'} text-truncate`">
          User: {{ userName }}
        </div>
        <v-spacer />
        <v-btn
          v-if="userName != null"
          class="mt-1 px-2 pr-3"
          color="secondary"
          :disabled="!isOnline"
          @click="signOut()"
        >
           <v-icon class="mr-1">mdi-exit-run</v-icon>
          Sign Out
        </v-btn>
      </div>

      <div class="pl-0 d-flex flex-wrap"> 
        <v-col class="mr-2 px-0 pb-0" :cols="breakpoint === 'xs' ? '3' : '2'">
          <v-select
            v-model="currentFarm"
            label="Current Farm"
            :disabled="!isOnline"
            :items="farms"
          />
        </v-col>
        <v-col class="mx-0 px-0 pb-0" :cols="breakpoint === 'xs' ? '3' : '2'">
            <v-text-field
              readonly
              label="Role"
              v-model="userRole"
            />
        </v-col>
        <v-spacer />
        <v-btn
          color="primary"
          class="mt-5 white--text"
          @click="reloadApp()"
          :disabled="!isOnline">
          <v-icon class="mr-1">mdi-reload</v-icon>
          Reload
        </v-btn>
      </div>

      <v-divider class="mb-4" />

      <div class="text-subtitle-1">Display:</div>
      <div class="d-flex flex-wrap">
        <v-switch 
          v-model="darkMode"
          inset
          class="pa-0 mt-1 ml-2 mr-2"
          label="Dark Mode"
        />
      </div>
      <div class="d-flex flex-wrap"> 
        <!-- <v-btn 
          class="mt-2 white--text"
          color="primary"
          @click="lockOrientation()">
           <v-icon class="mr-1">mdi-lock</v-icon>Lock
        </v-btn> -->
        <v-col class="pl-2 pb-0" :cols="breakpoint === 'xs' ? '8' : '4'">
          <v-select
            :disabled="!isMobileDevice"
            class="ml-0 pa-0"
            v-model="orientation"
            label="Mobile Orientation"
            :items="orientationTypes"
          />
        </v-col>
      </div>

      <v-divider class="mt-0 mb-4" />

      <div class="my-0">
        <div class="text-subtitle-1">Sounds:</div>
        <div class="d-flex">
          <v-switch 
            class="mt-1 ml-1"
            v-model="isSuccessSoundEnabled"
            inset
            label="Success Sound"
          />
          <v-spacer />
          <v-btn
            color="primary"
            class="white--text"
            :disabled="!isSuccessSoundEnabled"
            @click="makeSuccessSound()"
          >
            <v-icon>mdi-volume-high</v-icon>
            <div>Success</div>
          </v-btn>
        </div>
        <div class="d-flex">
          <v-switch
            class="mt-1 ml-1"
            v-model="isErrorSoundEnabled"
            inset
            label="Error Sound"
          />
          <v-spacer />
          <v-btn
            :disabled="!isErrorSoundEnabled"
            color="primary"
            class="white--text"
            @click="makeErrorSound()"
          >
            <v-icon>mdi-volume-high</v-icon>
            <div class="pr-5">Error</div>
          </v-btn>
        </div>
        <div class="d-flex">
          <v-switch
            class="mt-1 ml-1"
            v-model="isWarningSoundEnabled"
            inset
            label="Warning Sound"
          />
          <v-spacer />
          <v-btn
            :disabled="!isWarningSoundEnabled"
            color="primary"
            class="white--text"
            @click="makeWarningSound()"
          >
            <v-icon>mdi-volume-high</v-icon>
            <div class="pr-5">Warning</div>
          </v-btn>
        </div>
      </div>
      
      <v-divider class="mb-4" />

      <div class="my-0">
        <div class="text-subtitle-1">Printer:</div>     
        <div class="d-flex">
          <v-chip 
            v-if = printerConnected 
            class="mt-2 ml-2"  
            color = "success">
            <v-icon>
              mdi-printer
            </v-icon>
          </v-chip>
          <v-chip 
            v-else 
            class="mt-2 ml-2" 
            color = "error">
            <v-icon>
              mdi-printer-off
            </v-icon>
          </v-chip>
          <v-chip 
            v-if = printerConnected 
            class ="mt-2 ml-2">
            <v-icon
              :color = batteryLevelColor>
              {{batteryLevelIcon}}
            </v-icon>
             {{ printerData.batteryLevelPercentage }}%
          </v-chip>
          <v-chip 
            v-if = printerConnected
            class="ml-2 mt-2" >
            <v-icon
              :color = supplyLevelColor>
              mdi-paper-roll
            </v-icon>
              {{ printerData.supplyRemainingPercentage }}%
          </v-chip>
          <v-spacer />
          <v-btn
            color="primary"
            class="mt-2 px-2 white--text"
            @click="connectPrinter()">
            <v-icon>
              mdi-printer
            </v-icon>
            <div class="pr-5">
              Connect
            </div>
          </v-btn>
        </div>
        <div class="d-flex">
          <v-switch 
            class="mt-4 ml-2"
            v-model="autoCut"
            inset
            label="Auto Cut"
          />
          <v-spacer />
          <v-btn
            color="secondary"
            class="mt-3 px-1 white--text"
            @click="disconnectPrinter()">
            <v-icon>mdi-printer-off</v-icon>
            Disconnect
          </v-btn>
        </div>
        <div v-if = printerConnected>
          <v-chip class="ml-2 mt-1">
            Printer Name: {{ printerData.name }}
          </v-chip>
          <div class="d-flex">
            <v-chip class="ml-2 mt-1">
              Printer Model: {{ printerData.model }}
            </v-chip>
          </div>       
          <div class="d-flex">
            <v-chip class="ml-2 mt-1" >
              Supply Name: {{ printerData.supplyName }}
            </v-chip>
          </div>
        </div>
      </div>

      <v-divider class="mt-4 mb-4" />
      <div class="my-0">
        <div class="text-subtitle-1">Label:</div>
        <div class="d-flex">
        <img
          contain
          height='120px'
          width="155px"
          :src="labelSample"
          alt="Label Sample"
        />
        <v-spacer />
        <!-- <v-col cols="12" lg="2" md="3" sm="4"> -->
          <label-select-dialog
            @close-label-select-dialog="refreshLabelSample"
          />
        <!-- </v-col> -->
        </div>
      </div>


      <v-divider class="mt-4 mb-4" />
      <div>
        <div class="ml-2 text-subtitle-1 text-no-wrap">Api Url:</div>
        <div style="word-break: break-word" class="text-subtitle-2  text-truncate ml-5 mt-1">{{ apiUrl }}</div>
      </div>

      <v-divider class="my-4" />
    </v-card-text>
  </div>
</template>
<script>
import appVersion from '../../package.json'
import { mapState, mapActions } from 'vuex'
// eslint-disable-next-line no-unused-vars
import printerIcons from '../utils/printerIcons'
import LabelManager from '../utils/LabelManager'
import labelSelectDialog from '../components/LabelSelectDialog.vue'
import { ref } from 'vue'

export default {
  components: {
    'label-select-dialog': labelSelectDialog
  },
  data() {
    return {
      appVersion: appVersion,
      isBusy: false,
      orientationTypes: ['natural', 'portrait-primary', 'portrait-secondary', 'landscape-primary', 'landscape-secondary'],
      labelSample: ref(null)
    }
  },
  // mounted() {
  //     window.addEventListener("orientationchange", this.handleOrientationChange);
  // },
  async created() {
    // this.orientation = screen.orientation.type;
    this.refreshLabelSample();
  },
  computed: {
    ...mapState(['settings', 'isOnline', 'userProfile', 'printer', 'printerConnected', 'printerData']),
    apiUrl() {
      return process.env.VUE_APP_ROOT_API_SERVER_URL;
    },
    breakpoint() {
      return this.$vuetify.breakpoint.name;
    },
    currentFarm: {
      get() { return this.settings.currentFarm; },
      async set(value) {
        try {
          this.isBusy = true;
          const settings = { ...this.settings };
          settings.currentFarm = value;
          await this.loadFarmSettingsFromServer(settings);
          this.isBusy = false;
        }
        catch(error) {
          this.$root.showNoticeError(error.message);
        }
      }
    },
    isMobileDevice() {
      var isMobile = navigator.maxTouchPoints ? true: false;
      return isMobile;
    },
    farms() {
      return this.settings.farms.filter(f=>f.isEnabled == true).map(x=> x.farmCode);
    },
    darkMode: {
      get() { return this.settings.isDarkMode === true; },
      set(value) {
        const settings = { ...this.settings };
        settings.isDarkMode = value;
        this.updateSettingsInStore(settings);
        this.$vuetify.theme.dark = value;
        window.dispatchEvent(new CustomEvent('cep-set-scrollbar-theme'));
      }
    },
    isErrorSoundEnabled: {
      get() { return this.settings.isErrorSoundEnabled === true; },
      set(value) {
        const settings = { ...this.settings };
        settings.isErrorSoundEnabled = value;
        this.updateSettingsInStore(settings);
      }
    },
    isSuccessSoundEnabled: {
      get() { return this.settings.isSuccessSoundEnabled === true; },
      set(value) {
        const settings = { ...this.settings };
        settings.isSuccessSoundEnabled = value;
        this.updateSettingsInStore(settings);
      }
    },
    isWarningSoundEnabled: {
      get() { 
        return this.settings.isWarningSoundEnabled === true; 
      },
      set(value) {
        const settings = { ...this.settings };
        settings.isWarningSoundEnabled = value;
        this.updateSettingsInStore(settings);
      }
    },
    orientation: {
      get() {
        var orientation = this.settings.orientation;
        return (orientation ? orientation : 'natural');
      },
      set(value) {
        const settings = { ...this.settings };
        settings.orientation = value;
        this.updateSettingsInStore(settings);
      }
    },
    userName() {
      if (this.userProfile) {
        //var userName = this.userProfile.userPrincipalName.split('@')[0];
        var userName = this.userProfile.displayName;
        return userName;
      }
      else return null;
    },
    userRole() {
      if (this.settings.userContext) {
        var userRole = this.settings.userContext.contextName;
        return userRole;
      }
      else return null;
    },
    supplyLevelColor() {
      return printerIcons.getSupplyLevelColor(this.printerData.supplyRemainingPercentage);
    },
    batteryLevelColor() {
      return printerIcons.getBatteryLevelColor(this.printerData.batteryLevelPercentage);
    },
    batteryLevelIcon() {
      return printerIcons.getBatteryLevelIcon(this.printerData.batteryLevelPercentage, this.printerData.acCharging);
    },
    autoCut: {
      get() { 
        return this.settings.autoCut; 
      },
      set(value) {
        const settings = { ...this.settings };
        settings.autoCut = value;
        this.updateSettingsInStore(settings);
        this.printer.setCutOption(value ? 1 : 2);
      }
    }
  },
  methods: {
    ...mapActions(['updateSettingsInStore', 'loadFarmSettingsFromServer', 'storeSignOut', 'updatePrinterConnectionStatus']),
    async signOut() {
      await this.storeSignOut();
    },
    // handleOrientationChange() {
    //   this.orientation = screen.orientation.type;
    // },
    makeSuccessSound() {
      if (this.isSuccessSoundEnabled) {
        const audio = new Audio('sound/success-chime.mp3');
        audio.play();
      }
    },
    async refreshLabelSample() {
      const labelManager = new LabelManager();
      const label = await labelManager.generateLabel(this.settings.label, "0123", "LNUM", "HNUM", 480, 620, false);
      this.labelSample = URL.createObjectURL(label);
    },
    async connectPrinter() {
      const success = await this.printer.connect();
      success
        ? this.updatePrinterConnectionStatus()
        : this.$root.showNoticeError('Printer connection failed, if bluetooth light is solid hold the power down for 5 seconds until it is blinking again.');
      
    },
    async disconnectPrinter() {
      const success = await this.printer.disconnect();
      success
        ? this.$root.showNoticeSuccess('Printer disconnected successfully.')
        : this.$root.showNoticeError('Printer disconnection failed. This includes attempting to disconnect with no printer connected.');
    },
    // async lockOrientation() {
    //   try {
    //     if (this.orientation == "natural") {
    //       document.webkitExitFullscreen();
    //       await screen.orientation.unlock();
    //     }
    //     else {
    //       const body = document.getElementsByTagName("BODY")[0];
    //       body.webkitRequestFullscreen();
    //       await screen.orientation.lock(this.orientation);
    //     }
    //   }
    //   catch (error) {
    //     this.orientation = screen.orientation.type;
    //     this.$root.showNoticeError(error.message);
    //   }
    // },
    makeErrorSound() {
      if (this.isErrorSoundEnabled) {
        const audio = new Audio('sound/error-beep.mp3');
        audio.play();
      }
    },
    makeWarningSound() {
      if (this.isWarningSoundEnabled) {
        const audio = new Audio('sound/warning-beep.mp3');
        audio.play();
      }
    },
    reloadApp() {
      window.location = process.env.BASE_URL;
    }
  }
}
</script>